import React, { useEffect, useRef, useState } from "react";
import Footer from "features/Footer";
import AuthenticationService from "application/modules/authentication/AuthenticationService";
import CTLDashboardLayout from "../CTLDashboardLayout";
import {
  useMaterialUIController,
  setLayout,
  setActivePage,
  setClickToCall,
  setOpenPhone,
  setSearchText,
} from "context";
import CTLNavbar from "../CTLNavBar";
import layouts from "globals/layouts";
import loggerFactory from "globals/logger/logger-factory";
import { QueryClient, QueryClientProvider } from "react-query";
import { application, externalCallServer, telemetry } from "globals/endpoints";
import { resolveRoleLanding } from "globals/configuration/roleDefaultMenus";
import MDBox from "components/MDBox";
import SIPPhoneService from "../SIPPhone/SIPPhoneService";
import CTLAgentNavbar from "../CTLAgentNavbar";
import WebRTCPhone from "../Map/WebRTCPhone";
import { Grid } from "@mui/material";

const queryClient = new QueryClient();

const registeredCallbacks = {
  openAdvancedFilter: null,
};

function DefaultAuthorisedPage() {
  const componentName = "DefaultAuthorisedPage";
  const [controller, dispatch] = useMaterialUIController();
  const { activePage, searchText, AgentData } = controller;
  const [mode, setMode] = useState("0");

  const isWebView21 = () => {
    return (
      /wv|WebView|Edge\/\d+/i.test(navigator.userAgent) ||
      window.isWebView21 === true
    );
  };

  const [pageAttributes, setPageAttributes] = useState({
    enableAddButton: false,
    enableSendButton: false,
    enableSyncButton: false,
    enableSipSelect: false,
    enableNMSSelect: false,
    enableAddAgentButton: false,
    enableDefaulComparisontButton: false,
    enableNoDefaultComparisonButton: false,
    enableAllList: false,
    enablePrintButton: false,
    enableFileExportButton: false,
    enableFileImportButton: false,
    enableFilePreViewButton: false,
    enablePriPublish: false,
    enablePaste: false,
    enableChatButton: false,
    enableMarkasReadButton: false,
    enableMultipleDeleteButton: false,
    enableDownloadButton: false,
    enableUploadFaxButton: false,
    enableCollapsibleMenu: false,
    enableDownloadPdf: false,
    enableDownloadBillPdf: false,
    enableUpdateTableRecords: false,
    enableMultipleUpdate: false,
    enableApiAccess: false,
    enableExitButton: false,
    enableExternalApp: false,
    enableRefresh: false,
    enableViewAll: false,
    enableLogout: false,
    modes: [],
  });

  useEffect(() => {
    setLayout(
      dispatch,
      AuthenticationService.isAuthenticated() ? "dashboard" : "page",
    );
  }, []);

  function applyLandingPage(page) {
    activePage.key = page.key;
    activePage.name = page.name;
    activePage.url = page.url;
    activePage.description = page.description;
    setActivePage(dispatch, activePage);
  }

  // Landing-only hasDashboard probe: runs its own dashboard/list check for
  // the matched roleLandingConfig rule and dispatches the resolved page.
  // DashboardController and every ordinary (non-landing) dashboard.controller
  // entry point are left untouched and keep their unconditional mount fetch.
  useEffect(() => {
    const registered = layouts.some(
      (layout) => layout.pageId === activePage.key,
    );
    if (registered) {
      return;
    }

    const landing = resolveRoleLanding();
    if (landing.noPage || !landing.hasDashboard) {
      return;
    }

    let cancelled = false;

    telemetry
      .get("dashboard/list")
      .then((response) => {
        if (cancelled) {
          return;
        }
        const dashboards = response?.data?.data?.dashboards;
        applyLandingPage(
          Array.isArray(dashboards) && dashboards.length > 0
            ? landing.page
            : landing.fallbackPage,
        );
      })
      .catch((error) => {
        loggerFactory.error(
          componentName,
          "Unable to resolve landing dashboard availability",
          error,
        );
        if (!cancelled) {
          applyLandingPage(landing.fallbackPage);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [activePage.key]);

  function onLoad(attributes) {
    setPageAttributes(attributes);
  }

  function handleModeChange(mode) {
    setMode(mode);
  }

  function ExitPage() {
    if (registeredCallbacks.ExitPage) {
      registeredCallbacks.ExitPage();
    }
  }

  function onSendEmail() {
    if (registeredCallbacks.onSendEmail) {
      registeredCallbacks.onSendEmail();
    }
  }

  function onSendSMS() {
    if (registeredCallbacks.onSendSMS) {
      registeredCallbacks.onSendSMS();
    }
  }

  function onReportFilter() {
    if (registeredCallbacks.onReportFilter) {
      registeredCallbacks.onReportFilter();
    }
  }

  function onBackup() {
    if (registeredCallbacks.onBackup) {
      registeredCallbacks.onBackup();
    }
  }

  function onUpload() {
    if (registeredCallbacks.onUpload) {
      registeredCallbacks.onUpload();
    }
  }

  function handleSearchTextChange(text) {
    setSearchText(dispatch, text);
  }

  function CollapsibleMenuOpen() {
    if (registeredCallbacks.CollapsibleMenuOpen) {
      registeredCallbacks.CollapsibleMenuOpen();
    }
  }

  function PriPublish() {
    if (registeredCallbacks.PriPublish) {
      registeredCallbacks.PriPublish();
    }
  }
  function onRefresh() {
    if (registeredCallbacks.onRefresh) {
      registeredCallbacks.onRefresh();
    }
  }
  function onViewAll() {
    if (registeredCallbacks.onViewAll) {
      registeredCallbacks.onViewAll();
    }
  }

  function Paste() {
    if (registeredCallbacks.Paste) {
      registeredCallbacks.Paste();
    }
  }

  function MultipleUpdate() {
    if (registeredCallbacks.MultipleUpdate) {
      registeredCallbacks.MultipleUpdate();
    }
  }
  function DownloadPdf() {
    if (registeredCallbacks.DownloadPdf) {
      registeredCallbacks.DownloadPdf();
    }
  }

  function DownloadBillPdf() {
    if (registeredCallbacks.DownloadBillPdf) {
      registeredCallbacks.DownloadBillPdf();
    }
  }

  function UpdateTableRecords() {
    if (registeredCallbacks.UpdateTableRecords) {
      registeredCallbacks.UpdateTableRecords();
    }
  }
  function DownloadData() {
    if (registeredCallbacks.DownloadData) {
      registeredCallbacks.DownloadData();
    }
  }

  function DownloadDetailedData() {
    if (registeredCallbacks.DownloadDetailedData) {
      registeredCallbacks.DownloadDetailedData();
    }
  }

  function DownloadSelected() {
    if (registeredCallbacks.DownloadSelected) {
      registeredCallbacks.DownloadSelected();
    }
  }
  function onAdvancedFilter() {
    if (registeredCallbacks.openAdvancedFilter) {
      registeredCallbacks.openAdvancedFilter();
    }
  }

  function onOpenForm() {
    if (registeredCallbacks.openForm) {
      registeredCallbacks.openForm();
    }
  }

  function onPublish() {
    if (registeredCallbacks.onPublish) {
      registeredCallbacks.onPublish();
    }
  }
  function onChat() {
    if (registeredCallbacks.onChat) {
      registeredCallbacks.onChat();
    }
  }

  function onUploadFax() {
    if (registeredCallbacks.onUploadFax) {
      registeredCallbacks.onUploadFax();
    }
  }

  function onFilePreView() {
    if (registeredCallbacks.onFilePreView) {
      registeredCallbacks.onFilePreView();
    }
  }

  function onFileExport() {
    if (registeredCallbacks.onFileExport) {
      registeredCallbacks.onFileExport();
    }
  }

  function onFileImport() {
    if (registeredCallbacks.onFileImport) {
      registeredCallbacks.onFileImport();
    }
  }

  function onSendMessage() {
    if (registeredCallbacks.onSendMessage) {
      registeredCallbacks.onSendMessage();
    }
  }
  function onMarkasRead() {
    if (registeredCallbacks.onMarkasRead) {
      registeredCallbacks.onMarkasRead();
    }
  }
  function onMultipleDelete() {
    if (registeredCallbacks.onMultipleDelete) {
      registeredCallbacks.onMultipleDelete();
    }
  }
  function onSync() {
    if (registeredCallbacks.onSync) {
      registeredCallbacks.onSync();
    }
  }

  function onNMSSelect() {
    if (registeredCallbacks.onNMSSelect) {
      registeredCallbacks.onNMSSelect();
    }
  }

  function onAgent() {
    if (registeredCallbacks.onAgent) {
      registeredCallbacks.onAgent();
    }
  }
  function onAllList() {
    if (registeredCallbacks.onAllList) {
      registeredCallbacks.onAllList();
    }
  }
  function onDefaultComparison() {
    if (registeredCallbacks.onDefaultComparison) {
      registeredCallbacks.onDefaultComparison();
    }
  }
  function onNoDefaultComparison() {
    if (registeredCallbacks.onNoDefaultComparison) {
      registeredCallbacks.onNoDefaultComparison();
    }
  }

  function onPrint() {
    if (registeredCallbacks.onPrint) {
      registeredCallbacks.onPrint();
    }
  }
  function onEnableApiAccess() {
    if (registeredCallbacks.onEnableApiAccess) {
      registeredCallbacks.onEnableApiAccess();
    }
  }

  function LogoutStatus() {
    if (registeredCallbacks.LogoutStatus) {
      registeredCallbacks.LogoutStatus();
    }
  }

  function onCall(dialNumber) {
    if (dialNumber) {
      if (isWebView21()) {
        loggerFactory.debug("click to call event data send : ", dialNumber);
        window?.chrome?.webview?.postMessage(
          JSON.stringify({ eventType: "Call", data: dialNumber }),
        );
        if (AuthenticationService.allowAgentMenus()) {
          activePage.key = "agentDashboard";
          activePage.name = "Agent Dashboard";
          activePage.url = "home/app/agent dashboard";
          activePage.description = "";
          setActivePage(dispatch, activePage);
        }
      } else {
        if (
          AuthenticationService.getDeskPhoneStatus() ||
          !SIPPhoneService ||
          !SIPPhoneService.enabled
        ) {
          application.post("/externalApi/call", {
            caller: AuthenticationService.getExtensionNumber(),
            callee: dialNumber,
            predefinedAction: window.callServerPredefinedAction,
            callerUser: AuthenticationService.getFullName(),
          });
        } else {
          {
            !AuthenticationService.allowAgentMenus() &&
              !AuthenticationService.allowUCConferenceMenus() &&
              setOpenPhone(dispatch, true);
          }
          setClickToCall(dispatch, dialNumber);
          loggerFactory.debug(componentName, "Click to call : ", dialNumber);
        }
      }
    }
  }

  function registerCallBacks(callbacks) {
    registeredCallbacks.openAdvancedFilter = callbacks.openAdvancedFilter;
    registeredCallbacks.openForm = callbacks.openForm;
    registeredCallbacks.onPublish = callbacks.onPublish;
    registeredCallbacks.onChat = callbacks.onChat;
    registeredCallbacks.onFileExport = callbacks.onFileExport;
    registeredCallbacks.onFileImport = callbacks.onFileImport;
    registeredCallbacks.onFilePreView = callbacks.onFilePreView;
    registeredCallbacks.onSendMessage = callbacks.onSendMessage;
    registeredCallbacks.onMultipleDelete = callbacks.onMultipleDelete;
    registeredCallbacks.onMarkasRead = callbacks.onMarkasRead;
    registeredCallbacks.onSync = callbacks.onSync;
    registeredCallbacks.onAgent = callbacks.onAgent;
    registeredCallbacks.onAllList = callbacks.onAllList;
    registeredCallbacks.onDefaultComparison = callbacks.onDefaultComparison;
    registeredCallbacks.onNoDefaultComparison = callbacks.onNoDefaultComparison;
    registeredCallbacks.onPrint = callbacks.onPrint;
    registeredCallbacks.PriPublish = callbacks.PriPublish;
    registeredCallbacks.Paste = callbacks.Paste;
    registeredCallbacks.onRefresh = callbacks.onRefresh;
    registeredCallbacks.onViewAll = callbacks.onViewAll;
    registeredCallbacks.DownloadPdf = callbacks.DownloadPdf;
    registeredCallbacks.DownloadBillPdf = callbacks.DownloadBillPdf;
    registeredCallbacks.DownloadData = callbacks.DownloadData;
    registeredCallbacks.DownloadDetailedData = callbacks.DownloadDetailedData;
    registeredCallbacks.CollapsibleMenuOpen = callbacks.CollapsibleMenuOpen;
    registeredCallbacks.DownloadSelected = callbacks.DownloadSelected;
    registeredCallbacks.onUploadFax = callbacks.onUploadFax;
    registeredCallbacks.MultipleUpdate = callbacks.MultipleUpdate;
    registeredCallbacks.onEnableApiAccess = callbacks.onEnableApiAccess;
    registeredCallbacks.ExitPage = callbacks.ExitPage;
    registeredCallbacks.UpdateTableRecords = callbacks.UpdateTableRecords;
    registeredCallbacks.LogoutStatus = callbacks.LogoutStatus;
    registeredCallbacks.onSendEmail = callbacks.onSendEmail;
    registeredCallbacks.onSendSMS = callbacks.onSendSMS;
    registeredCallbacks.onReportFilter = callbacks.onReportFilter;
    registeredCallbacks.onBackup = callbacks.onBackup;
    registeredCallbacks.onUpload = callbacks.onUpload;
  }
  const renderComponent = (currentPage) => {
    let returnValue = null;

    layouts.map((layout) => {
      if (layout.pageId == currentPage) {
        returnValue = layout.component;
      }
    });

    if (!returnValue) {
      const landing = resolveRoleLanding();

      if (landing.noPage) {
        // Explicit legacy no-page branches (Hotel, KM Admin, Department
        // Head, EAMS), evaluated at their original ladder positions. EAMS's
        // silentPage preserves the original quirk of mutating activePage's
        // fields without ever dispatching them.
        if (landing.silentPage) {
          activePage.key = landing.silentPage.key;
          activePage.name = landing.silentPage.name;
          activePage.url = landing.silentPage.url;
          activePage.description = landing.silentPage.description;
        }
        return null;
      }

      if (!landing.hasDashboard) {
        applyLandingPage(landing.page);
      }
      // hasDashboard:true landings resolve asynchronously via the landing
      // probe effect, which dispatches the final page once dashboard/list
      // is known; render nothing this pass.
    }

    return returnValue;
  };

  let componentToRender = renderComponent(activePage.key);

  return (
    <CTLDashboardLayout>
      <QueryClientProvider client={queryClient}>
        <>
          {AuthenticationService.allowAgentMenus() ? (
            <CTLAgentNavbar
              pageAttributes={pageAttributes}
              onModeSelected={handleModeChange}
              onSearchTextChange={handleSearchTextChange}
              onAdvancedFilter={onAdvancedFilter}
              onOpenForm={onOpenForm}
              onPublish={onPublish}
              onChat={onChat}
              onFileExport={onFileExport}
              onFileImport={onFileImport}
              onSendMessage={onSendMessage}
              onMultipleDelete={onMultipleDelete}
              onMarkasRead={onMarkasRead}
              onSync={onSync}
              onNMSSelect={onNMSSelect}
              onAgent={onAgent}
              onAllList={onAllList}
              onDefaultComparison={onDefaultComparison}
              onNoDefaultComparison={onNoDefaultComparison}
              onPrint={onPrint}
              onFilePreView={onFilePreView}
              PriPublish={PriPublish}
              Paste={Paste}
              onRefresh={onRefresh}
              onViewAll={onViewAll}
              DownloadPdf={DownloadPdf}
              DownloadBillPdf={DownloadBillPdf}
              DownloadData={DownloadData}
              CollapsibleMenuOpen={CollapsibleMenuOpen}
              DownloadSelected={DownloadSelected}
              onUploadFax={onUploadFax}
              UpdateTableRecords={UpdateTableRecords}
              MultipleUpdate={MultipleUpdate}
              onEnableApiAccess={onEnableApiAccess}
              ExitPage={ExitPage}
              LogoutStatus={LogoutStatus}
              onSendEmail={onSendEmail}
              onSendSMS={onSendSMS}
              onReportFilter={onReportFilter}
              onBackup={onBackup}
              onUpload={onUpload}
            />
          ) : (
            <CTLNavbar
              pageAttributes={pageAttributes}
              onModeSelected={handleModeChange}
              onSearchTextChange={handleSearchTextChange}
              onAdvancedFilter={onAdvancedFilter}
              onOpenForm={onOpenForm}
              onPublish={onPublish}
              onChat={onChat}
              onFileExport={onFileExport}
              onFileImport={onFileImport}
              onSendMessage={onSendMessage}
              onMultipleDelete={onMultipleDelete}
              onMarkasRead={onMarkasRead}
              onSync={onSync}
              onNMSSelect={onNMSSelect}
              onAgent={onAgent}
              onAllList={onAllList}
              onDefaultComparison={onDefaultComparison}
              onNoDefaultComparison={onNoDefaultComparison}
              onPrint={onPrint}
              onFilePreView={onFilePreView}
              PriPublish={PriPublish}
              Paste={Paste}
              onRefresh={onRefresh}
              onViewAll={onViewAll}
              DownloadPdf={DownloadPdf}
              DownloadBillPdf={DownloadBillPdf}
              DownloadData={DownloadData}
              DownloadDetailedData={DownloadDetailedData}
              CollapsibleMenuOpen={CollapsibleMenuOpen}
              DownloadSelected={DownloadSelected}
              onUploadFax={onUploadFax}
              UpdateTableRecords={UpdateTableRecords}
              MultipleUpdate={MultipleUpdate}
              onEnableApiAccess={onEnableApiAccess}
              ExitPage={ExitPage}
              LogoutStatus={LogoutStatus}
              onSendEmail={onSendEmail}
              onSendSMS={onSendSMS}
              onReportFilter={onReportFilter}
              onBackup={onBackup}
              onUpload={onUpload}
            />
          )}

          {(AuthenticationService.allowRailwayDispatcher() ||
            AuthenticationService.allowMCXDispatcher()) &&
          !isWebView21() ? (
            <MDBox sx={{ mt: 0.5 }}>
              <Grid container spacing={1}>
                <Grid item xs={12} lg={8} xl={9.5}>
                  <MDBox
                    sx={{
                      height: "100%",
                      pr: 0.5,
                      overflowY: "auto",
                      scrollbarWidth: "none",
                      "&::-webkit-scrollbar": {
                        display: "none",
                      },
                    }}
                  >
                    {componentToRender &&
                      React.createElement(componentToRender, {
                        id: activePage.appId,
                        onLoad: onLoad,
                        mode: mode,
                        pageId: activePage.key,
                        searchText: searchText,
                        registerCallBacks: registerCallBacks,
                        onCall: onCall,
                        pageName: activePage.name,
                      })}
                  </MDBox>
                </Grid>
                <Grid item xs={12} lg={4} xl={2.5}>
                  <MDBox
                    sx={{
                      height: "100%",
                      overflow: "auto",
                      position: "relative",
                     
                    }}
                  >
                    {/* <MAPPhone /> */}
                    <WebRTCPhone />
                  </MDBox>
                </Grid>
              </Grid>
            </MDBox>
          ) : (
            <MDBox sx={{ mt: 0.5 }}>
              {componentToRender &&
                React.createElement(componentToRender, {
                  id: activePage.appId,
                  onLoad: onLoad,
                  mode: mode,
                  pageId: activePage.key,
                  searchText: searchText,
                  registerCallBacks: registerCallBacks,
                  onCall: onCall,
                  pageName: activePage.name,
                })}
            </MDBox>
          )}
        </>
      </QueryClientProvider>
    </CTLDashboardLayout>
  );
}

export default DefaultAuthorisedPage;
